import { createApi } from '@reduxjs/toolkit/query/react';

import { Rainmaker } from '../types/rainmaker-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const rainmakerApiClient = createApi({
  reducerPath: 'rainmakerApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Rainmaker', 'Tasks'] as const,
  endpoints: (builder) => ({
    getRainmakerByTask: builder.query<Rainmaker | null, { taskId: string }>({
      query: ({ taskId }) => `api/rainmaker/task/${taskId}/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'Rainmaker', id: taskId },
      ],
      transformResponse: (response: Rainmaker | null) => {
        // Backend returns 204 No Content when no rainmaker exists
        // RTK Query will transform this to null
        return response;
      },
    }),
    executeSuggestion: builder.mutation<Rainmaker, { suggestionId: string }>({
      query: ({ suggestionId }) => ({
        url: `api/rainmaker/suggestions/${suggestionId}/execute/`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        // Invalidate Rainmaker cache by task_id so the query refetches
        ...(result ? [{ type: 'Rainmaker' as const, id: result.task_id }] : []),
        // Also invalidate the Task cache so task status updates are reflected
        ...(result ? [{ type: 'Tasks' as const, id: result.task_id }] : []),
      ],
    }),
    dismissSuggestion: builder.mutation<Rainmaker, { suggestionId: string }>({
      query: ({ suggestionId }) => ({
        url: `api/rainmaker/suggestions/${suggestionId}/dismiss/`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        // Invalidate by task_id so the query refetches
        ...(result ? [{ type: 'Rainmaker' as const, id: result.task_id }] : []),
      ],
    }),
    restoreRainmaker: builder.mutation<Rainmaker, { rainmakerId: string }>({
      query: ({ rainmakerId }) => ({
        url: `api/rainmaker/${rainmakerId}/restore/`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        // Invalidate by task_id so the query refetches
        ...(result ? [{ type: 'Rainmaker' as const, id: result.task_id }] : []),
      ],
    }),
  }),
});

export const {
  useGetRainmakerByTaskQuery,
  useExecuteSuggestionMutation,
  useDismissSuggestionMutation,
  useRestoreRainmakerMutation,
} = rainmakerApiClient;
